/**
 * Surveys (Beta) list — Surveys API `/api/v1/surveys`.
 * See surveys-beta.gaps.ts for omitted PDF controls.
 */
import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
} from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Search } from 'lucide-react';
import { debounce } from 'lodash';
import {
  PortalDsButton,
  PortalDsSegmented,
  PortalDsSelect,
  portalDs,
} from 'src/components/portal-design-system';
import { showNotification } from 'src/redux/notification/notification.slice';
import { useDispatch } from 'react-redux';
import {
  useArchiveSurveysApiV1SurveyMutation,
  useCloneSurveysApiV1SurveyMutation,
  useCreateSurveysApiV1SurveyMutation,
  useGetSurveysApiV1ListQuery,
} from 'src/redux/survey/surveys-api-v1/surveys-api-v1.endpoints';
import { isEditableSurveyStatus } from 'src/redux/survey/surveys-api-v1/surveys-api-v1.adapters';
import { SurveysBetaTable } from './components/surveys-beta-table';
import { useSurveysBetaExtClientId } from './hooks/use-surveys-beta-ext-client-id';
import {
  SURVEYS_BETA_PAGE_SIZE,
  SURVEYS_BETA_STATUS_OPTIONS,
  SURVEYS_BETA_TYPE_OPTIONS,
  parseStatusFilter,
  parseTitleFilter,
  parseTypeFilter,
  statusFilterToApiStatuses,
  typeFilterToApiTypes,
  type SurveysBetaStatusFilter,
  type SurveysBetaTypeFilter,
} from './utils/surveys-beta-filters';
import {
  surveysBetaEditPath,
  surveysBetaResultsPath,
} from './utils/surveys-beta-navigation';
import './surveys-beta.gaps';

const SurveysBetaTab = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { clientId } = useParams<{ clientId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const extClientId = useSurveysBetaExtClientId();
  const statusFilter = parseStatusFilter(searchParams.get('status'));
  const titleFilter = parseTitleFilter(searchParams.get('title'));
  const typeFilter = parseTypeFilter(searchParams.get('type'));

  const [cursorStack, setCursorStack] = useState<Array<string | null>>([null]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [searchValue, setSearchValue] = useState(titleFilter);

  const pageIndex = cursorStack.length - 1;
  const currentCursor = cursorStack[pageIndex] ?? null;

  const listArg = useMemo(
    () => ({
      surveyStatus: statusFilterToApiStatuses(statusFilter),
      surveyType: typeFilterToApiTypes(typeFilter),
      title: titleFilter || undefined,
      limit: SURVEYS_BETA_PAGE_SIZE,
      cursor: currentCursor,
      extClientId,
    }),
    [statusFilter, typeFilter, titleFilter, currentCursor, extClientId]
  );

  const { data, isFetching, isError, refetch } = useGetSurveysApiV1ListQuery(listArg);
  const [createSurvey, { isLoading: isCreating }] = useCreateSurveysApiV1SurveyMutation();
  const [cloneSurvey, { isLoading: isCloning }] = useCloneSurveysApiV1SurveyMutation();
  const [archiveSurvey, { isLoading: isArchiving }] = useArchiveSurveysApiV1SurveyMutation();

  const items = data?.items ?? [];
  const hasMore = Boolean(data?.page.hasMore);
  const nextCursor = data?.page.nextCursor ?? null;

  const resetPaging = () => {
    setCursorStack([null]);
    setSelectedIds(new Set());
  };

  const setStatusFilter = (value: SurveysBetaStatusFilter) => {
    const next = new URLSearchParams(searchParams);
    if (value === 'all') {
      next.delete('status');
    } else {
      next.set('status', value);
    }
    setSearchParams(next, { replace: true });
    resetPaging();
  };

  const setTypeFilter = (value: SurveysBetaTypeFilter) => {
    const next = new URLSearchParams(searchParams);
    if (value === 'all') {
      next.delete('type');
    } else {
      next.set('type', value);
    }
    setSearchParams(next, { replace: true });
    resetPaging();
  };

  const debouncedTitleSearch = useRef(
    debounce((value: string, params: URLSearchParams) => {
      const next = new URLSearchParams(params);
      const trimmed = value.trim();
      if (!trimmed) {
        next.delete('title');
      } else {
        next.set('title', trimmed);
      }
      setSearchParams(next, { replace: true });
      setCursorStack([null]);
      setSelectedIds(new Set());
    }, 300)
  ).current;

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchValue(value);
    debouncedTitleSearch(value, searchParams);
  };

  const handleRowClick = useCallback(
    (extSurveyId: string, status: string) => {
      if (isEditableSurveyStatus(status)) {
        navigate(surveysBetaEditPath(extSurveyId, clientId));
        return;
      }
      navigate(surveysBetaResultsPath(extSurveyId, clientId));
    },
    [clientId, navigate]
  );

  const handleToggleSelect = (extSurveyId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(extSurveyId)) {
        next.delete(extSurveyId);
      } else {
        next.add(extSurveyId);
      }
      return next;
    });
  };

  const handleToggleSelectAll = () => {
    setSelectedIds((prev) => {
      if (items.length > 0 && items.every((item) => prev.has(item.ext_survey_id))) {
        return new Set();
      }
      return new Set(items.map((item) => item.ext_survey_id));
    });
  };

  const handleNewSurvey = async() => {
    try {
      const created = await createSurvey({
        body: { title: 'Untitled' },
        extClientId,
        idempotencyKey:
          typeof crypto !== 'undefined' && 'randomUUID' in crypto
            ? crypto.randomUUID()
            : undefined,
      }).unwrap();
      navigate(surveysBetaEditPath(created.ext_survey_id, clientId));
    } catch (error) {
      const message =
        (error as { message?: string })?.message || 'Could not create survey';
      dispatch(showNotification({ summary: message, theme: 'warning', sticky: true }));
    }
  };

  const handleDuplicateSelected = async() => {
    const ids = Array.from(selectedIds);
    try {
      for (const extSurveyId of ids) {
        await cloneSurvey({ extSurveyId, extClientId }).unwrap();
      }
      setSelectedIds(new Set());
      refetch();
      dispatch(
        showNotification({
          summary: ids.length === 1 ? 'Survey duplicated' : `${ids.length} surveys duplicated`,
          theme: 'success',
        })
      );
    } catch (error) {
      const message =
        (error as { message?: string })?.message || 'Could not duplicate survey';
      dispatch(showNotification({ summary: message, theme: 'warning', sticky: true }));
    }
  };

  const handleArchiveSelected = async() => {
    const ids = Array.from(selectedIds);
    try {
      for (const extSurveyId of ids) {
        await archiveSurvey({ extSurveyId, extClientId }).unwrap();
      }
      setSelectedIds(new Set());
      refetch();
      dispatch(
        showNotification({
          summary: ids.length === 1 ? 'Survey archived' : `${ids.length} surveys archived`,
          theme: 'success',
        })
      );
    } catch (error) {
      const message =
        (error as { message?: string })?.message || 'Could not archive survey';
      dispatch(showNotification({ summary: message, theme: 'warning', sticky: true }));
    }
  };

  const showingCount = items.length;
  const bulkBusy = isCloning || isArchiving;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className={portalDs.sheetTitle}>
            Surveys
          </h1>
          <p className={portalDs.sheetDescription}>
            Surveys (Beta) · Surveys API
            {showingCount > 0 ? ` · showing ${showingCount}` : ''}
            {hasMore ? ' · more available' : ''}
          </p>
        </div>
        <PortalDsButton
          variant="accent"
          onClick={handleNewSurvey}
          disabled={isCreating}
        >
          New survey
        </PortalDsButton>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
        <div className={`${portalDs.searchBar} w-full sm:max-w-sm`}>
          <Search
            className={portalDs.searchIcon}
            aria-hidden
          />
          <input
            type="search"
            className={portalDs.searchInput}
            placeholder="Search surveys"
            value={searchValue}
            onChange={handleSearchChange}
            aria-label="Search surveys by title"
          />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <PortalDsSegmented
            ariaLabel="Filter by status"
            options={SURVEYS_BETA_STATUS_OPTIONS}
            value={statusFilter}
            onChange={setStatusFilter}
          />
          <div className="w-36">
            <PortalDsSelect
              aria-label="Filter by survey type"
              value={typeFilter}
              onChange={(event) =>
                setTypeFilter(parseTypeFilter(event.target.value))
              }
            >
              {SURVEYS_BETA_TYPE_OPTIONS.map((option) => (
                <option
                  key={option.value}
                  value={option.value}
                >
                  {option.label}
                </option>
              ))}
            </PortalDsSelect>
          </div>
        </div>
      </div>

      {selectedIds.size > 0 ? (
        <div className={portalDs.selectionBar}>
          <span className="text-[13.5px] font-semibold text-[#0D0F1A]">
            {selectedIds.size}
            {' '}
            selected
          </span>
          <PortalDsButton
            size="sm"
            variant="secondary"
            disabled={bulkBusy}
            onClick={handleDuplicateSelected}
          >
            Duplicate
          </PortalDsButton>
          <PortalDsButton
            size="sm"
            variant="dangerGhost"
            disabled={bulkBusy}
            onClick={handleArchiveSelected}
          >
            Archive
          </PortalDsButton>
          <PortalDsButton
            size="sm"
            variant="ghost"
            onClick={() => setSelectedIds(new Set())}
          >
            Clear selection
          </PortalDsButton>
        </div>
      ) : null}

      <SurveysBetaTable
        items={items}
        selectedIds={selectedIds}
        onToggleSelect={handleToggleSelect}
        onToggleSelectAll={handleToggleSelectAll}
        onRowClick={handleRowClick}
        isLoading={isFetching}
        isError={isError}
      />

      <div className="flex flex-wrap items-center justify-between gap-3">
        <span className={portalDs.rowCount}>
          Page
          {' '}
          {pageIndex + 1}
          {hasMore ? ' · more pages' : ''}
        </span>
        <div className="flex gap-2">
          <PortalDsButton
            size="sm"
            variant="secondary"
            disabled={pageIndex === 0 || isFetching}
            onClick={() => {
              setCursorStack((stack) => (stack.length > 1 ? stack.slice(0, -1) : stack));
              setSelectedIds(new Set());
            }}
          >
            Previous
          </PortalDsButton>
          <PortalDsButton
            size="sm"
            variant="secondary"
            disabled={!hasMore || !nextCursor || isFetching}
            onClick={() => {
              if (!nextCursor) {
                return;
              }
              setCursorStack((stack) => [...stack, nextCursor]);
              setSelectedIds(new Set());
            }}
          >
            Next
          </PortalDsButton>
        </div>
      </div>
    </div>
  );
};

export default SurveysBetaTab;
